import { Injectable } from '@angular/core';
import type html2canvas from 'html2canvas';

/**
 * Turns the on-screen invite card into an image the host can actually send.
 *
 * Before this, every share button shipped something other than what was on the
 * screen — the raw QR JPEG or a bare URL — so what landed in the guest's chat
 * was a naked black square with no event name and no instruction. The only way
 * to send the card was to screenshot it.
 *
 * html2canvas is loaded on demand so it stays out of the initial bundle.
 */
@Injectable({ providedIn: 'root' })
export class InviteCardExportService {
  private lib?: typeof html2canvas;

  private async load(): Promise<typeof html2canvas> {
    if (!this.lib) {
      const mod = await import('html2canvas');
      this.lib = ((mod as any).default || mod) as typeof html2canvas;
    }
    return this.lib;
  }

  /**
   * Rasterises `el` to a PNG File, or null if anything goes wrong — callers
   * fall back to the old QR-only path rather than leaving the button dead.
   *
   * Render this ahead of time, not inside the click handler. navigator.share()
   * needs transient activation, and a tap that spends a second rasterising has
   * already spent it by the time it asks.
   */
  /**
   * @param targetWidth width of the saved image in pixels. 1400 is A6 at a bit
   *   over 300dpi, which is what the printed card is for — the element itself
   *   is only ~250px on screen, so the scale is derived rather than fixed.
   * @param type PNG for the printed card (crisp QR). JPEG for the link-preview
   *   card: a photo-led 1200×1200 card is several times smaller as JPEG, which
   *   matters because WhatsApp fetches it on the host's phone while they type.
   */
  async toFile(
    el: HTMLElement,
    fileName: string,
    targetWidth = 1400,
    background = '#FBF0EA',
    type: 'image/png' | 'image/jpeg' = 'image/png',
    // 0.80 keeps a busy 1200² card well under the ~300KB WhatsApp is known to
    // skip previews above; the difference from 0.86 isn't visible at chat size.
    quality = 0.8,
  ): Promise<File | null> {
    try {
      // Each invite-card template sets its own webfont (see invite-card-templates.ts).
      // If the face hasn't landed yet, html2canvas quietly rasterises the
      // fallback and the exported card looks nothing like the one on screen.
      await (document as any).fonts?.ready;

      const html2canvas = await this.load();
      const canvas = await html2canvas(el, {
        // Derived from the element's own width so shrinking the on-screen card
        // cannot quietly shrink the print. Floored at 2 so a stray zero width
        // never produces a one-pixel image.
        scale: Math.max(2, targetWidth / (el.offsetWidth || targetWidth)),
        // The card's own ground, so its rounded corners blend away instead of
        // leaving white notches on whatever the image is pasted onto.
        backgroundColor: background,
        // useCORS but deliberately not allowTaint: a tainted canvas makes
        // toBlob throw, which turns a missing image into a dead button.
        useCORS: true,
        logging: false,
      });

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), type, type === 'image/jpeg' ? quality : undefined)
      );
      return blob ? new File([blob], fileName, { type }) : null;
    } catch {
      return null;
    }
  }

  /** Saves a prepared file to disk. */
  download(file: File): void {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
  }

  canShareFile(file: File): boolean {
    return !!navigator.share && !!(navigator as any).canShare?.({ files: [file] });
  }

  /** True if the sheet opened; false if the platform refused or the host dismissed it. */
  async share(file: File, title: string, text: string): Promise<boolean> {
    if (!this.canShareFile(file)) return false;
    try {
      await navigator.share({ title, text, files: [file] });
      return true;
    } catch {
      return false;
    }
  }
}
